import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { APP_NAME } from '../../config/branding';
import { INSIGHT_SURFACE_ACCESS } from '../../config/insightAccess';
import { useAssistant } from '../../contexts/AssistantContext';
import { useAuth } from '../../contexts/AuthContext';
import { navigateToSettings } from '../../core/navigation/navigationRef';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import { ApiError } from '../../services/apiClient';
import type { AssistantMessage } from '../../services/assistantService';
import {
  listPinnedWidgets,
  pinFieldsFromAssistantContent,
  pinWidget,
} from '../../services/pinnedWidgetService';
import {
  notifyPinnedWidgetsChanged,
  subscribePinnedWidgetsChanged,
} from '../../services/pinnedWidgetsBus';
import { telemetry } from '../../services/telemetry';
import { KeyboardAvoidingContainer } from '../layout/KeyboardAvoidingContainer';
import { GlassButton } from '../ui/GlassButton';
import { AssistantAvatar, AssistantIcon } from './AssistantIcons';
import { ChatMarkdown } from './ChatMarkdown';

const STARTERS = [
  { label: 'FIRE number', prompt: "What's my FIRE number and how do I get there?" },
  { label: 'Cut spend', prompt: 'How do I make my monthly expenses go lower?' },
  { label: 'Home loan', prompt: 'How can I pay off my home loan faster?' },
  { label: 'Subscriptions', prompt: 'What subscriptions do I have?' },
];

/** Kill the bright browser focus ring on web Pressables. */
const noFocusRing: ViewStyle | null =
  Platform.OS === 'web'
    ? ({ outlineWidth: 0, outlineColor: 'transparent' } as ViewStyle)
    : null;

function formatMsgTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatThreadTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * Messaging-style assistant sheet — ChatGPT/iMessage layout with markdown + voice.
 */
export function AssistantHost() {
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const { isAuthenticated } = useAuth();
  const {
    open,
    minimized,
    busy,
    streaming,
    loadingContext,
    error,
    thread,
    threads,
    historyOpen,
    pinnedInsightTitle,
    canChat,
    openAssistant,
    closeAssistant,
    minimizeAssistant,
    expandAssistant,
    sendMessage,
    startFresh,
    resumeThread,
    refreshThreadList,
    setHistoryOpen,
  } = useAssistant();

  const [draft, setDraft] = useState('');
  const [pinningId, setPinningId] = useState<string | null>(null);
  const [pinnedMessageIds, setPinnedMessageIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [pinFeedback, setPinFeedback] = useState<string | null>(null);
  const scrollRef = useRef<ScrollView>(null);
  const messages = (thread?.messages ?? []).filter(
    (m) => m.role === 'user' || m.role === 'assistant',
  );
  const isBusy = busy || streaming;
  const wide = width >= 768;
  const sheetWidth = wide ? Math.min(420, width - 40) : width;
  const sheetHeight = wide ? Math.min(720, height - 32) : height;

  const speech = useSpeechToText({
    onFinal: (text) => {
      setDraft((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
    },
  });

  useEffect(() => {
    if (!open || minimized || historyOpen) return;
    scrollRef.current?.scrollToEnd({ animated: true });
  }, [open, minimized, historyOpen, messages.length, messages[messages.length - 1]?.content]);

  useEffect(() => {
    if (!open || minimized || canChat) return;
    void telemetry.logPaywallView('assistant_chat');
  }, [open, minimized, canChat]);

  useEffect(() => {
    setPinFeedback(null);
  }, [thread?.id]);

  useEffect(() => {
    if (!open || !isAuthenticated) return;
    let cancelled = false;
    const loadPinnedIds = async () => {
      try {
        const res = await listPinnedWidgets();
        if (cancelled) return;
        const ids = new Set(
          (res.pinnedWidgets ?? [])
            .map((p) => p.sourceMessageId)
            .filter((id): id is string => Boolean(id)),
        );
        setPinnedMessageIds(ids);
      } catch {
        // non-blocking — pin buttons still work
      }
    };
    void loadPinnedIds();
    const unsub = subscribePinnedWidgetsChanged(() => {
      void loadPinnedIds();
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [open, isAuthenticated, thread?.id]);

  const handlePinMessage = async (message: AssistantMessage) => {
    if (!message.id || !message.content.trim() || pinningId) return;
    setPinningId(message.id);
    setPinFeedback(null);
    try {
      const { title, body } = pinFieldsFromAssistantContent(message.content);
      const metaWidget =
        typeof thread?.metadata?.widget === 'string' ? thread.metadata.widget : undefined;
      const target =
        metaWidget && metaWidget in INSIGHT_SURFACE_ACCESS ? metaWidget : undefined;
      const res = await pinWidget({
        title,
        body,
        sourceMessageId: message.id,
        sourceThreadId: thread?.id,
        ...(target ? { target } : {}),
      });
      setPinnedMessageIds((prev) => new Set(prev).add(message.id!));
      setPinFeedback(res.alreadyPinned ? 'Already on home' : 'Pinned to home');
      notifyPinnedWidgetsChanged();
    } catch (err) {
      const msg =
        err instanceof ApiError && err.message
          ? err.message
          : 'Could not pin — try again';
      setPinFeedback(msg);
    } finally {
      setPinningId(null);
    }
  };

  if (!isAuthenticated) return null;

  const fabBottom = Math.max(insets.bottom, spacing.md) + 88;
  const panelVisible = open && !minimized;
  const threadLabel = pinnedInsightTitle || thread?.title?.trim() || null;

  const submitDraft = () => {
    const text = draft.trim();
    if (!text || isBusy) return;
    if (!canChat) {
      void telemetry.logUpgradeCtaClick('assistant_chat');
      navigateToSettings();
      return;
    }
    setDraft('');
    void sendMessage(text);
  };

  return (
    <>
      {!panelVisible ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Open assistant chat"
          onPress={() => {
            if (minimized) {
              expandAssistant();
              return;
            }
            void openAssistant();
          }}
          style={({ pressed }) => [
            styles.fab,
            { bottom: fabBottom },
            pressed && styles.pressed,
            noFocusRing,
          ]}
        >
          <View style={styles.fabInner}>
            <AssistantAvatar size={28} />
            <Text style={styles.fabText}>Chat</Text>
          </View>
        </Pressable>
      ) : null}

      <Modal
        visible={panelVisible}
        transparent
        animationType={wide ? 'fade' : 'slide'}
        onRequestClose={closeAssistant}
      >
        <KeyboardAvoidingContainer>
          <View style={styles.modalRoot}>
          <Pressable
            style={styles.backdrop}
            onPress={minimizeAssistant}
            accessibilityLabel="Dismiss assistant"
          />
          <View
            style={[
              styles.sheet,
              wide
                ? {
                    width: sheetWidth,
                    height: sheetHeight,
                    marginRight: 20,
                    marginBottom: 20,
                    borderRadius: radii.xl,
                  }
                : {
                    width: sheetWidth,
                    flex: 1,
                    paddingTop: insets.top,
                  },
            ]}
          >
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.headerLeft}>
                <AssistantAvatar size={36} />
                <View style={styles.headerTitles}>
                  <Text style={styles.headerTitle}>Assistant</Text>
                  <Text style={styles.headerSub} numberOfLines={1}>
                    {threadLabel || (canChat ? APP_NAME : 'Pro required to chat')}
                  </Text>
                </View>
              </View>
              <View style={styles.headerActions}>
                {canChat ? (
                  <>
                    <IconBtn
                      label={historyOpen ? 'Back to chat' : 'Chat history'}
                      onPress={() => {
                        const next = !historyOpen;
                        setHistoryOpen(next);
                        if (next) void refreshThreadList();
                      }}
                    >
                      <AssistantIcon
                        name={historyOpen ? 'back' : 'history'}
                        color={colors.textMuted}
                      />
                    </IconBtn>
                    <IconBtn
                      label="New chat"
                      disabled={isBusy}
                      onPress={() => void startFresh()}
                    >
                      <AssistantIcon name="new" color={colors.textMuted} />
                    </IconBtn>
                  </>
                ) : null}
                <IconBtn label="Close" onPress={closeAssistant}>
                  <AssistantIcon name="close" color={colors.textMuted} />
                </IconBtn>
              </View>
            </View>

            {!canChat ? (
              <View style={styles.upgradeBanner}>
                <Text style={styles.upgradeTitle}>Unlock chat with Pro</Text>
                <Text style={styles.upgradeBody}>
                  Insight summaries stay free. Upgrade to discuss your numbers with the assistant.
                </Text>
                <GlassButton
                  variant="primary"
                  size="sm"
                  onPress={() => {
                    void telemetry.logUpgradeCtaClick('assistant_chat');
                    closeAssistant();
                    navigateToSettings();
                  }}
                >
                  Upgrade to Pro
                </GlassButton>
              </View>
            ) : null}

            {historyOpen && canChat ? (
              <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.historyPad}
                keyboardShouldPersistTaps="handled"
              >
                <Text style={styles.sectionLabel}>Recent</Text>
                {threads.length === 0 ? (
                  <Text style={styles.muted}>No chats yet.</Text>
                ) : (
                  threads.map((t) => {
                    const active = thread?.id === t.id;
                    return (
                      <Pressable
                        key={t.id}
                        onPress={() => void resumeThread(t.id)}
                        style={({ pressed }) => [
                          styles.historyRow,
                          active && styles.historyRowActive,
                          pressed && styles.pressed,
                          noFocusRing,
                        ]}
                      >
                        <Text style={styles.historyTitle} numberOfLines={2}>
                          {t.title?.trim() || 'Untitled chat'}
                        </Text>
                        <Text style={styles.historyMeta}>{formatThreadTime(t.updatedAt)}</Text>
                      </Pressable>
                    );
                  })
                )}
              </ScrollView>
            ) : (
              <ScrollView
                ref={scrollRef}
                style={styles.scroll}
                contentContainerStyle={styles.messagesPad}
                keyboardShouldPersistTaps="handled"
                onContentSizeChange={() =>
                  scrollRef.current?.scrollToEnd({ animated: true })
                }
              >
                {!messages.length ? (
                  <View style={styles.empty}>
                    <AssistantAvatar size={48} />
                    <Text style={styles.emptyTitle}>Ask anything about your money</Text>
                    <Text style={styles.emptySub}>
                      Spend, savings, FIRE, subscriptions — grounded in your MoneyLens data.
                    </Text>
                    {canChat ? (
                      <View style={styles.chips}>
                        {STARTERS.map((s) => (
                          <Pressable
                            key={s.label}
                            disabled={isBusy}
                            onPress={() => void openAssistant({ starterPrompt: s.prompt })}
                            style={({ pressed }) => [
                              styles.chip,
                              pressed && styles.pressed,
                              noFocusRing,
                            ]}
                          >
                            <Text style={styles.chipText}>{s.label}</Text>
                          </Pressable>
                        ))}
                      </View>
                    ) : null}
                  </View>
                ) : (
                  messages.map((m, i) => {
                    const isUser = m.role === 'user';
                    const isLastAssistant =
                      m.role === 'assistant' && i === messages.length - 1;
                    const showCursor = streaming && isLastAssistant;
                    const canPin =
                      !isUser &&
                      Boolean(m.id) &&
                      Boolean(m.content.trim()) &&
                      !(streaming && isLastAssistant);
                    const isPinned = Boolean(m.id && pinnedMessageIds.has(m.id));
                    return (
                      <View
                        key={m.id ?? `${m.role}-${m.createdAt}-${i}`}
                        style={[styles.msgRow, isUser ? styles.msgRowUser : styles.msgRowAi]}
                      >
                        {!isUser ? (
                          <View style={styles.aiMeta}>
                            <AssistantAvatar size={22} />
                            <Text style={styles.aiName}>Assistant</Text>
                          </View>
                        ) : null}
                        {isUser ? (
                          <View style={styles.userBubble}>
                            <Text style={styles.userText}>{m.content}</Text>
                          </View>
                        ) : (
                          <View style={styles.aiBody}>
                            <ChatMarkdown content={m.content} cursor={showCursor} />
                            {canPin ? (
                              <Pressable
                                onPress={() => void handlePinMessage(m)}
                                disabled={pinningId === m.id || isPinned}
                                style={({ pressed }) => [
                                  styles.pinBtn,
                                  pressed && styles.pressed,
                                  noFocusRing,
                                ]}
                                accessibilityRole="button"
                                accessibilityLabel={
                                  isPinned ? 'Pinned to home' : 'Pin to home'
                                }
                              >
                                <Text style={styles.pinBtnText}>
                                  {pinningId === m.id
                                    ? 'Pinning…'
                                    : isPinned
                                      ? 'Pinned'
                                      : 'Pin to home'}
                                </Text>
                              </Pressable>
                            ) : null}
                          </View>
                        )}
                        <Text style={[styles.time, isUser && styles.timeUser]}>
                          {formatMsgTime(m.createdAt)}
                        </Text>
                      </View>
                    );
                  })
                )}
                {pinFeedback ? (
                  <Text style={styles.pinFeedback} accessibilityLiveRegion="polite">
                    {pinFeedback}
                  </Text>
                ) : null}
                {loadingContext ? (
                  <View style={styles.contextLoading}>
                    <ActivityIndicator color={colors.accent} />
                    <Text style={styles.contextLoadingText}>
                      Loading spend, portfolio, home loan, and profile…
                    </Text>
                  </View>
                ) : null}
                {busy && !streaming && !loadingContext ? (
                  <ActivityIndicator color={colors.accent} style={styles.loader} />
                ) : null}
                {error ? (
                  <Text style={styles.error} accessibilityRole="alert">
                    {error}
                  </Text>
                ) : null}
              </ScrollView>
            )}

            {!historyOpen ? (
              <View
                style={[
                  styles.composerBar,
                  { paddingBottom: Math.max(insets.bottom, 12) },
                ]}
              >
                {speech.listening && speech.interim ? (
                  <Text style={styles.interim} numberOfLines={2}>
                    {speech.interim}
                  </Text>
                ) : null}
                {speech.error ? <Text style={styles.error}>{speech.error}</Text> : null}
                <View style={styles.composer}>
                  {speech.supported && canChat ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={
                        speech.listening ? 'Stop voice input' : 'Start voice input'
                      }
                      onPress={() => (speech.listening ? speech.stop() : speech.start())}
                      style={({ pressed }) => [
                        styles.iconRound,
                        speech.listening && styles.iconRoundActive,
                        pressed && styles.pressed,
                        noFocusRing,
                      ]}
                    >
                      <AssistantIcon
                        name="mic"
                        size={18}
                        color={speech.listening ? colors.accent : colors.textMuted}
                      />
                    </Pressable>
                  ) : null}
                  <TextInput
                    value={draft}
                    onChangeText={setDraft}
                    placeholder={canChat ? 'Message…' : 'Upgrade to send messages…'}
                    placeholderTextColor={colors.textMuted}
                    style={styles.input}
                    editable={!isBusy && canChat}
                    multiline
                    maxLength={4000}
                    returnKeyType="send"
                    onSubmitEditing={submitDraft}
                    blurOnSubmit={false}
                  />
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={canChat ? 'Send message' : 'Upgrade to Pro'}
                    disabled={isBusy || (!draft.trim() && canChat)}
                    onPress={submitDraft}
                    style={({ pressed }) => [
                      styles.sendBtn,
                      (!draft.trim() && canChat) || isBusy
                        ? styles.sendBtnDisabled
                        : null,
                      pressed && styles.pressed,
                      noFocusRing,
                    ]}
                  >
                    <AssistantIcon
                      name="send"
                      size={18}
                      color={
                        (!draft.trim() && canChat) || isBusy
                          ? colors.textMuted
                          : colors.background
                      }
                    />
                  </Pressable>
                </View>
              </View>
            ) : null}
          </View>
        </View>
        </KeyboardAvoidingContainer>
      </Modal>
    </>
  );
}

function IconBtn({
  label,
  onPress,
  disabled,
  children,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      disabled={disabled}
      onPress={onPress}
      hitSlop={8}
      style={({ pressed }) => [
        styles.iconBtn,
        disabled && styles.iconBtnDisabled,
        pressed && !disabled && styles.pressed,
        noFocusRing,
      ]}
    >
      {children}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  fab: {
    position: 'absolute',
    right: spacing.md,
    zIndex: 50,
  },
  fabInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 52,
    paddingHorizontal: 14,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceElevated,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(61, 214, 164, 0.35)',
  },
  fabText: {
    ...typography.caption,
    fontWeight: '800',
    color: colors.accent,
  },
  modalRoot: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
  },
  sheet: {
    zIndex: 2,
    backgroundColor: colors.background,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    ...Platform.select({
      web: {
        boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
      } as object,
      default: {},
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    backgroundColor: colors.surface,
    gap: spacing.sm,
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 },
  headerTitles: { flex: 1, minWidth: 0, gap: 2 },
  headerTitle: {
    ...typography.headline,
    fontSize: 17,
    color: colors.text,
  },
  headerSub: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 12,
  },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  iconBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBtnDisabled: { opacity: 0.35 },
  upgradeBanner: {
    marginHorizontal: spacing.md,
    marginTop: spacing.md,
    padding: spacing.md,
    gap: spacing.sm,
    borderRadius: radii.lg,
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(61, 214, 164, 0.28)',
  },
  upgradeTitle: { ...typography.headline, fontSize: 15, color: colors.text },
  upgradeBody: {
    ...typography.body,
    fontSize: 13,
    color: colors.textMuted,
    lineHeight: 18,
  },
  scroll: { flex: 1, backgroundColor: colors.background },
  messagesPad: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
    gap: spacing.lg,
    flexGrow: 1,
  },
  historyPad: { padding: spacing.md, gap: spacing.sm },
  sectionLabel: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    marginBottom: 4,
  },
  muted: { ...typography.body, color: colors.textMuted, fontSize: 14 },
  empty: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingTop: spacing.xl,
    paddingHorizontal: spacing.md,
  },
  emptyTitle: {
    ...typography.headline,
    fontSize: 22,
    color: colors.text,
    textAlign: 'center',
    marginTop: spacing.sm,
  },
  emptySub: {
    ...typography.body,
    fontSize: 15,
    color: colors.textMuted,
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 320,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    justifyContent: 'center',
    marginTop: spacing.md,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
  },
  chipText: { ...typography.caption, fontWeight: '600', color: colors.text },
  msgRow: { gap: 6, maxWidth: '100%' },
  msgRowUser: { alignSelf: 'flex-end', alignItems: 'flex-end', maxWidth: '82%' },
  msgRowAi: { alignSelf: 'stretch', maxWidth: '100%' },
  aiMeta: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 2 },
  aiName: { ...typography.caption, fontWeight: '700', color: colors.textMuted },
  userBubble: {
    backgroundColor: colors.accent,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 18,
    borderBottomRightRadius: 6,
  },
  userText: {
    ...typography.body,
    fontSize: 15,
    lineHeight: 22,
    color: '#04140E',
    fontWeight: '500',
  },
  aiBody: {
    paddingRight: spacing.sm,
  },
  pinBtn: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: radii.pill,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(61, 214, 164, 0.35)',
    backgroundColor: 'rgba(61, 214, 164, 0.1)',
  },
  pinBtnText: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
  },
  pinFeedback: {
    ...typography.caption,
    color: colors.accent,
    marginTop: spacing.sm,
  },
  time: {
    ...typography.caption,
    fontSize: 11,
    color: colors.textMuted,
  },
  timeUser: { marginRight: 4 },
  loader: { marginTop: spacing.sm },
  contextLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.lg,
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(61, 214, 164, 0.25)',
  },
  contextLoadingText: {
    ...typography.body,
    flex: 1,
    fontSize: 14,
    color: colors.textMuted,
    lineHeight: 20,
  },
  error: { ...typography.caption, color: colors.danger, marginTop: 4 },
  composerBar: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
    paddingHorizontal: spacing.md,
    paddingTop: 10,
    backgroundColor: colors.surface,
    gap: 6,
  },
  interim: { ...typography.caption, color: colors.accent },
  composer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    backgroundColor: colors.surfaceElevated,
    borderRadius: radii.xl,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    paddingLeft: 6,
    paddingRight: 6,
    paddingVertical: 6,
  },
  iconRound: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconRoundActive: {
    backgroundColor: 'rgba(61, 214, 164, 0.16)',
  },
  input: {
    flex: 1,
    minHeight: 40,
    maxHeight: 120,
    paddingHorizontal: 8,
    paddingVertical: 10,
    color: colors.text,
    ...typography.body,
    fontSize: 15,
    lineHeight: 20,
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.accent,
  },
  sendBtnDisabled: {
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  historyRow: {
    paddingVertical: 14,
    paddingHorizontal: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    gap: 4,
  },
  historyRowActive: {
    borderColor: 'rgba(61, 214, 164, 0.45)',
    backgroundColor: 'rgba(61, 214, 164, 0.1)',
  },
  historyTitle: { ...typography.body, fontSize: 15, color: colors.text, fontWeight: '600' },
  historyMeta: { ...typography.caption, color: colors.textMuted },
  pressed: { opacity: 0.85 },
});
